<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { useNotification } from '@kyvg/vue3-notification'
import { price as formatNumberPrice } from '@/helpers/numberFormatters'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import FolderLockedModal from '@/ui-kit/courses/FolderLockedModal.vue'
import MaterialsPurchaseModal from '@/ui-kit/courses/MaterialsPurchaseModal.vue'
import VPurchaseSmartbook from '@/ui-kit/components/PurchaseSmartbook/PurchaseSmartbook.vue'
import { useStaticAsset } from '~/composables/useStaticAsset'

interface Author {
  name: string
  title?: string
  description?: string
  photo: string
}

interface PurchaseData {
  material_id: number
  material_name: string
  can_purchase_per_unit: boolean
  unit_id: number
  unit_name: string
  unit_price: number
  course_name: string
  course_price: number
  description: string
  discount_reason: string
  units_to_purchase: number
}

interface RelatedSeminar {
  id: number
  name: string
  type: string
  description_url: string
}

interface ChildItem {
  name: string
  url: string
  is_folder: boolean
  status: string | null
  icon?: string
  file_extension?: string
  size?: number
  karaoke?: boolean
  smartbook?: boolean
  vdo?: boolean
  purchase_data?: PurchaseData
  related_seminar?: RelatedSeminar
}

interface DiscountBanner {
  course_name: string
  discount: number
  promocode: string
  link: string
}

interface TeacherMaterialsContext {
  folder: { name: string; authors?: Author[] }
  path: Array<{ name: string; url: string }>
  children: ChildItem[]
  recents: Array<{ course_id: number; course_name: string; units: Array<{ name: string; url: string }> }> | null
  discount_banner: DiscountBanner | null
}

const ctx = usePageContext<TeacherMaterialsContext>()
const { isMobile } = useScreen()
const { notify } = useNotification()
const asset = useStaticAsset()

const breadcrumbs = computed(() => {
  return ctx.path.map(e => ({ label: e.name, href: e.url }))
})

const isScrolled = ref(false)
const modal = ref<{ type?: string; title?: string; data?: PurchaseData | RelatedSeminar }>({})
const smartbookModal = ref(false)

const paymentModal = ref<{
  open: (opts: { order: { name: string; price?: number }; query: Record<string, number> }) => void
} | null>(null)

const availableItems = computed(() => {
  return ctx.children.filter(e => e.status !== 'paid')
})

const paidMaterials = computed(() => {
  return ctx.children.filter(e => e.status === 'paid')
})

const recentFolders = computed(() => {
  if (!ctx.recents) return []
  return ctx.recents.map(item => ({
    key: item.course_id,
    title: item.course_name,
    href: `/courses/teacher_materials/course/${item.course_id}`,
    items: item.units.map(unit => ({
      label: unit.name,
      href: unit.url
    }))
  }))
})

const isRootDirectory = computed(() => {
  return breadcrumbs.value.length < 2
})

function formatRub(val: number): string {
  return formatNumberPrice.rub(val)
}

function navigateTo(url: string) {
  window.location.href = url
}

async function copyText(text: string) {
  if (!navigator.clipboard) {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.top = '0'
    textarea.style.left = '0'
    textarea.style.position = 'fixed'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()

    try {
      const successful = document.execCommand('copy')
      if (!successful) throw new Error('Copy command failed')
      notify({ type: 'success', title: 'Промокод скопирован!' })
    } catch (e) {
      console.error(e)
      notify({ type: 'error', title: 'Не удалось скопировать промокод.' })
    }

    document.body.removeChild(textarea)
    return
  }

  try {
    await navigator.clipboard.writeText(text)
    notify({ type: 'success', title: 'Промокод скопирован!' })
  } catch (e) {
    console.error(e)
    notify({ type: 'error', title: 'Не удалось скопировать промокод.' })
  }
}

function scrollHandler() {
  isScrolled.value = window.scrollY > 10
}

function clickHandler(item: ChildItem) {
  if (item.status === 'paid' && item.smartbook) {
    smartbookModal.value = true
    return
  }

  if (item.status === 'paid') {
    modal.value = {
      type: 'payment',
      title: 'Покупка',
      data: item.purchase_data
    }
  } else if (item.status === 'locked') {
    modal.value = {
      type: 'locked',
      title: 'Недоступно',
      data: item.related_seminar
    }
  }
}

function getIcon(item: ChildItem): string | undefined {
  if (item.smartbook) return 'smartbook-icon'
  if (item.status === 'locked') return 'folders-locked'
  if (item.status === 'paid') return 'files-block'
  if (item.is_folder) return 'folder'
  return undefined
}

function getFileInfo(item: ChildItem): { name: string; ext: string; size?: number } | null {
  if (item.is_folder || item.smartbook || item.karaoke) return null
  if (item.vdo) {
    return { name: item.name, ext: 'mp4' }
  }
  return {
    name: item.name,
    ext: item.file_extension ? item.file_extension.slice(1) : '',
    size: item.size
  }
}

function purchase({ mode, data }: { mode: string; data: PurchaseData }) {
  const { material_name, material_id } = data
  const { unit_id, unit_name, unit_price } = data
  const { course_name, course_price } = data
  modal.value = {}

  const order: { name: string; price?: number } = {
    name: `\u00AB${material_name}\u00BB`
  }

  if (mode === 'unit') {
    order.name += ` для юнита \u00AB${unit_name}\u00BB`
    order.price = unit_price
  }

  if (mode === 'course') {
    order.name += ` для всего курса \u00AB${course_name}\u00BB`
    order.price = course_price
  }

  const query: Record<string, number> = { material_id }
  if (mode === 'unit') {
    query.unit_id = unit_id
  }

  paymentModal.value?.open({ order, query })
}

onMounted(() => {
  window.addEventListener('scroll', scrollHandler)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', scrollHandler)
})
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" rules-url="/website/documents/oferta" />

    <div class="teacher-materials content">
      <v-notifications position="bottom right" />

      <div
        v-if="!isMobile"
        class="teacher-materials__breadcrumbs"
        :class="{ 'teacher-materials__breadcrumbs_scrolled': isScrolled }"
      >
        <v-breadcrumbs :items="breadcrumbs" />
      </div>

      <div class="teacher-materials__header">
        <h1 class="teacher-materials__title" v-text="ctx.folder.name" />
      </div>

      <div class="teacher-materials__content">
        <div class="teacher-materials__folders">
          <v-card-button
            v-for="item in availableItems"
            :key="item.url"
            :file="getFileInfo(item)"
            :href="item.status === 'locked' ? null : item.url"
            :target="item.is_folder ? null : '_blank'"
            @click="clickHandler(item)"
          >
            {{ item.name }}

            <template #icon>
              <img
                v-if="item.icon"
                :class="{ 'teacher-materials__avatar': !isRootDirectory && !item.karaoke }"
                :src="item.icon"
                width="48"
                height="48"
              />
              <v-icon v-else size="48">
                {{ getIcon(item) }}
              </v-icon>
            </template>
          </v-card-button>

          <template v-if="paidMaterials.length">
            <div class="teacher-materials__folder-subheader">Платные материалы</div>

            <v-card-button v-for="item in paidMaterials" :key="item.url" :file="getFileInfo(item)">
              {{ item.name }}

              <template #action>
                <v-button mode="primary block" @click="clickHandler(item)"> Купить </v-button>
              </template>

              <template #icon>
                <img v-if="item.icon" class="teacher-materials__avatar" :src="item.icon" width="48" height="48" />
                <v-icon v-else size="48">
                  {{ getIcon(item) }}
                </v-icon>
              </template>
            </v-card-button>
          </template>
        </div>

        <div class="teacher-materials__spacer" />

        <div class="teacher-materials__authors-container">
          <div v-for="(author, i) in ctx.folder.authors" :key="i" class="teacher-materials__author mb-2">
            <div class="teacher-materials__author-info">
              <img class="teacher-materials__avatar" :src="author.photo" width="56" height="56" />
              <div class="d-flex flex-column">
                <span class="text-body-bold" v-text="author.name" />
                <span v-if="author.title" class="gray-text mt-half" v-text="author.title" />
              </div>
            </div>

            <p class="teacher-materials__author-description" v-text="author.description" />
          </div>

          <div v-if="ctx.discount_banner !== null" class="material-discount-banner">
            <img class="material-discount-banner__img" :src="asset('img/teacher-materials-discount-img.webp')" />

            <p class="material-discount-banner__upper">
              <span class="text-caption">
                Вы приобрели электронную версию наглядности {{ ctx.discount_banner.course_name }}, поэтому
              </span>

              <span class="text-title-bold"> Для вас скидка {{ formatRub(ctx.discount_banner.discount) }} </span>

              <span> на печатное наглядное пособие {{ ctx.discount_banner.course_name }} </span>
            </p>

            <p class="material-discount-banner__promo-card mt-3">
              <a class="material-discount-banner__promo-code" @click="() => copyText(ctx.discount_banner!.promocode)">
                {{ ctx.discount_banner.promocode }}
              </a>

              <span class="text-caption gray-text">
                Введите промокод на
                <v-link mode="primary" :href="ctx.discount_banner.link" target="_blank"> eng-shop.ru </v-link>
              </span>
            </p>

            <v-button mode="warn filled block" class="mt-2" :href="ctx.discount_banner.link" target="_blank">
              Купить сейчас
            </v-button>
          </div>

          <div v-if="ctx.recents" class="teacher-materials__recent">
            <p class="text-body">Недавно посещенные курсы:</p>
            <div class="teacher-materials__recent-tree">
              <v-folder-tree v-for="item in recentFolders" v-bind="item" :key="item.key" />
            </div>
          </div>
        </div>
      </div>

      <v-payment-modal ref="paymentModal" url="/courses/request_purchase_teacher_materials" />

      <v-modal :title="modal.title" :model-value="!!modal.type" @update:model-value="modal = {}">
        <MaterialsPurchaseModal
          v-if="modal.type === 'payment'"
          :purchase-data="modal.data as PurchaseData"
          @purchase="purchase"
          @close="() => (modal = {})"
        />
        <FolderLockedModal
          v-if="modal.type === 'locked'"
          :seminar-data="modal.data as RelatedSeminar"
          @close="() => (modal = {})"
        />
      </v-modal>

      <v-modal v-model="smartbookModal" title="Умный учебник">
        <div class="teacher-materials__smartbook-modal">
          <h3 class="text-h3 mr-d-6"><span v-if="!isMobile">Умный учебник: </span>Важная информация для учителей</h3>

          <p class="mt-3">Обратите внимание: теперь приобрести Умный учебник можно только через кабинет родителя.</p>

          <div class="mt-3">
            Приобретая учебник через кабинет родителя, вы получаете его полный функционал с доступом на 18 месяцев:

            <ul class="teacher-materials__bullet-list text-body-bold">
              <li>Умный учебник</li>
              <li>Кабинет аватара</li>
              <li>Магазин для покупок</li>
            </ul>
          </div>

          <p class="mt-3">
            А также, в кабинете учителя в разделе «Подготовка к урокам» вам навсегда будет доступна специальная
            учительская версия: только сам
            <b>Умный учебник без кабинета аватара и магазина, но с возможностью перематывать видеоуроки.</b>
          </p>

          <v-button mode="primary large block" class="mt-3" @click="() => navigateTo('/parent/cabinet')">
            Купить через кабинет родителя
          </v-button>
        </div>
      </v-modal>

      <VPurchaseSmartbook ref="purchaseSmartbook" :students="[]" :course-name="null" :course-id="null" :price="null" />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/courses/teacher_materials.scss' as *;
</style>
